import type { AppDataTableColumn } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import {
  AppBadge,
  AppMemberDisplay,
  AppSelectPopover,
  AppTooltip,
  type SelectOption,
} from '@9b3kuq5phwuk4txw/kiban-react-ui'
import type { OpportunityTask, OpportunityTaskStatus } from 'backend/types'
import { Edit2, Trash2 } from 'lucide-react'
import { COLOR_CATEGORY, getColor } from '@/lib/color-registry'
import { formatDate, toDateTime } from '@/lib/format-utils'

/**
 * 概要テキストを省略表示
 */
function truncateText(text: string | null, maxLength = 30): string {
  if (!text) return '-'
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}...`
}

/**
 * タスク一覧のカラム定義を生成
 */
export function createTaskColumns(params: {
  statuses: OpportunityTaskStatus[]
  editable: boolean
  onEdit: (task: OpportunityTask) => void
  onDelete: (task: OpportunityTask) => void
  onStatusChange: (taskId: number, statusId: number) => void
}): AppDataTableColumn<OpportunityTask>[] {
  const { statuses, editable, onEdit, onDelete, onStatusChange } = params

  const columns: AppDataTableColumn<OpportunityTask>[] = [
    {
      key: 'title',
      label: 'タイトル',
      accessor: (row) => row.title,
      sortable: false,
      cellClassName: 'min-w-[120px] font-medium',
      headerClassName: 'min-w-[120px]',
      required: true,
      active: true,
    },
    {
      key: 'description',
      label: '概要',
      accessor: (row) => row.description,
      sortable: false,
      render: (row) => {
        if (!row.description) return '-'
        const truncated = truncateText(row.description, 30)
        const isTruncated = row.description.length > 30

        if (!isTruncated) {
          return <span>{row.description}</span>
        }

        return (
          <AppTooltip content={<p className='whitespace-pre-wrap'>{row.description}</p>} className='max-w-[400px]'>
            <span className='cursor-help'>{truncated}</span>
          </AppTooltip>
        )
      },
      cellClassName: 'min-w-[120px]',
      headerClassName: 'min-w-[120px]',
      active: true,
    },
    {
      key: 'member_name',
      label: '担当者',
      accessor: (row) => row.member_name,
      sortable: false,
      render: (row) => <AppMemberDisplay employeeNumber={row.member_employee_number} name={row.member_name} />,
      cellClassName: 'min-w-[150px]',
      headerClassName: 'min-w-[150px]',
      active: true,
    },
    {
      key: 'expired_on',
      label: '期限',
      accessor: (row) => row.expired_on,
      sortable: false,
      render: (row) => (row.expired_on ? formatDate(row.expired_on) : '-'),
      headerRender: () => <div className='text-xs text-center w-full'>期限</div>,
      cellClassName: 'min-w-[100px] text-center',
      headerClassName: 'min-w-[100px]',
      active: true,
    },
    {
      key: 'opportunity_task_status_name',
      label: 'ステータス',
      accessor: (row) => row.opportunity_task_status_name,
      sortable: false,
      render: (row) => {
        if (editable) {
          return (
            <AppSelectPopover
              currentValue={String(row.opportunity_task_status_id)}
              options={statuses.map(
                (s): SelectOption => ({
                  value: String(s.id),
                  label: s.name,
                  color: getColor(COLOR_CATEGORY.TASK_STATUS, s.id),
                }),
              )}
              onSelect={(value) => onStatusChange(row.id, Number(value))}
              fallbackLabel={row.opportunity_task_status_name || 'Open'}
              triggerVariant='ghost'
              align='end'
            />
          )
        }

        return (
          <AppBadge color={getColor(COLOR_CATEGORY.TASK_STATUS, row.opportunity_task_status_id)}>
            {row.opportunity_task_status_name || 'Open'}
          </AppBadge>
        )
      },
      headerRender: () => <div className='text-xs text-center w-full'>ステータス</div>,
      cellClassName: 'min-w-[90px] text-center',
      headerClassName: 'min-w-[90px]',
      active: true,
    },
    {
      key: 'updated_by_name',
      label: '更新者',
      accessor: (row) => row.updated_by_name,
      sortable: false,
      render: (row) => (
        <AppMemberDisplay
          employeeNumber={row.updated_by_employee_number}
          name={row.updated_by_name}
          emptyDisplay='dash'
        />
      ),
      cellClassName: 'min-w-[150px]',
      headerClassName: 'min-w-[150px]',
      active: false,
    },
    {
      key: 'created_by_name',
      label: '作成者',
      accessor: (row) => row.created_by_name,
      sortable: false,
      render: (row) => (
        <AppMemberDisplay
          employeeNumber={row.created_by_employee_number}
          name={row.created_by_name}
          emptyDisplay='dash'
        />
      ),
      cellClassName: 'min-w-[150px]',
      headerClassName: 'min-w-[150px]',
      active: false,
    },
    {
      key: 'updated_at',
      label: '更新日時',
      accessor: (row) => row.updated_at,
      render: (row) => {
        if (!row.updated_at) return '-'
        return (
          <AppTooltip content={toDateTime(row.updated_at)?.toFormat('yyyy/MM/dd HH:mm:ss') ?? '-'}>
            <span className='cursor-help text-muted-foreground'>{formatDate(row.updated_at, 'relative')}</span>
          </AppTooltip>
        )
      },
      cellClassName: 'min-w-[70px]',
      headerClassName: 'min-w-[70px]',
      active: false,
    },
    {
      key: 'created_at',
      label: '作成日時',
      accessor: (row) => row.created_at,
      render: (row) => {
        if (!row.created_at) return '-'
        return (
          <AppTooltip content={toDateTime(row.created_at)?.toFormat('yyyy/MM/dd HH:mm:ss') ?? '-'}>
            <span className='cursor-help text-muted-foreground'>{formatDate(row.created_at, 'relative')}</span>
          </AppTooltip>
        )
      },
      cellClassName: 'min-w-[70px]',
      headerClassName: 'min-w-[70px]',
      active: false,
    },
  ]

  // editable の場合のみ Action カラムを追加
  if (editable) {
    columns.push({
      key: 'action',
      label: 'Action',
      accessor: () => null,
      sortable: false,
      render: (row) => (
        <div className='flex items-center justify-center gap-1'>
          <AppTooltip content='タスクの編集'>
            <button type='button' className='p-1 hover:bg-muted rounded' onClick={() => onEdit(row)}>
              <Edit2 className='h-4 w-4' />
            </button>
          </AppTooltip>
          <AppTooltip content='タスクの削除'>
            <button type='button' className='p-1 hover:bg-muted rounded' onClick={() => onDelete(row)}>
              <Trash2 className='h-4 w-4' />
            </button>
          </AppTooltip>
        </div>
      ),
      cellClassName: 'min-w-[80px] [&>div]:justify-center',
      headerClassName: 'min-w-[80px] text-center',
      required: true,
      active: true,
    })
  }

  return columns
}
